/**
 * StoreContext — reads /store/settings/public (no auth required) and exposes
 * runtime storefront state to the whole customer app:
 *   • storeStatus  (open/closed/holiday/maintenance) + custom message
 *   • announcement (top strip content, editable via Marketing panel)
 *   • zones + serviceable pincodes (delivery gate)
 *   • homepage.sections (order + toggle)
 *   • current selected pincode (localStorage)
 *   • derived: `canOrder`, `isServiceable`, `deliveryFee`, `freeOver`, `minOrder`
 *
 * Never blocks browsing. Only blocks *checkout* & the ADD button when the
 * store is closed or the current pincode is not serviceable.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { subscribePreview, isPreviewMode } from "@/lib/visualBuilder/previewBridge";

const StoreCtx = createContext(null);
const LS_PIN = "bb_delivery_pincode";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// Default homepage section order — Blinkit-style flow:
// hero → categories → hot deals → trending → recommended → best sellers →
// recently viewed → buy again → brands → festival → offers → app → CTA → trust.
// Sprint 58 — launch-critical order: Hero → Categories → Best Sellers → Trending → Others.
export const DEFAULT_HOMEPAGE_SECTIONS = [
  { key: "hero",            label: "Hero / slider",       enabled: true },
  { key: "categories",      label: "Categories",          enabled: true },
  { key: "best_sellers",    label: "Best sellers",        enabled: true },
  { key: "trending",        label: "Trending",            enabled: true },
  { key: "flash_deals",     label: "Hot deals",           enabled: true },
  { key: "middle_banner",   label: "Middle banner",       enabled: true },
  { key: "recommended",     label: "Recommended",         enabled: true },
  { key: "recently_viewed", label: "Recently viewed",     enabled: true },
  { key: "buy_again",       label: "Buy again",           enabled: true },
  { key: "all_products",    label: "All products",        enabled: true },
  { key: "brand_banner",    label: "Brand banners",       enabled: true },
  { key: "festival_banner", label: "Festival banners",    enabled: true },
  { key: "offer_banner",    label: "Offer banners",       enabled: true },
  { key: "app_banner",      label: "App download banner", enabled: true },
  { key: "bottom_banner",   label: "Bottom banner",       enabled: true },
  { key: "offer_cta",       label: "Big offer CTA",       enabled: true },
  { key: "trust_strip",     label: "Trust strip",         enabled: true },
];

export function StoreProvider({ children }) {
  const [store, setStore] = useState(null);
  const [ui, setUI] = useState(null); // UI Customization blob (SUPER_ADMIN configured)
  const [loading, setLoading] = useState(true);
  const [pincode, setPincodeState] = useState(() => {
    try { return localStorage.getItem(LS_PIN) || ""; } catch { return ""; }
  });

  const refresh = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        axios.get(`${API}/store/settings/public`).then((r) => r.data).catch(() => null),
        axios.get(`${API}/ui-customization`).then((r) => r.data).catch(() => null),
      ]);
      setStore(s);
      setUI(u);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Visual Builder preview bridge — when this page runs inside the
  // super-admin builder iframe, listen for postMessage updates and
  // hot-swap the `ui` blob. No network write, no persistence — the
  // Publish workflow in the builder is the only path to production.
  useEffect(() => {
    if (!isPreviewMode()) return;
    const unsub = subscribePreview(({ blob }) => {
      // Merge into existing UI so any keys not touched by the builder
      // (e.g. legacy header/pdp) keep their published values.
      setUI((prev) => ({ ...(prev || {}), ...(blob || {}) }));
    });
    return unsub;
  }, []);

  const setPincode = useCallback((pin) => {
    setPincodeState(pin || "");
    try { pin ? localStorage.setItem(LS_PIN, pin) : localStorage.removeItem(LS_PIN); } catch { /* noop */ }
  }, []);

  const commerce = store?.commerce || {};
  const marketing = store?.marketing || {};
  const legacyDelivery = store?.delivery || {};
  // Sprint 14: storefront style + social links (free-form under commerce)
  const social = commerce.social || {};
  const storefront = commerce.storefront || {};
  const categoryStyle = storefront.category_style || "circle";       // circle | rounded | square
  const categoryFont  = storefront.category_font  || "default";      // default | display | semibold | condensed
  const categoryLayout = storefront.category_layout || "auto";       // auto | horizontal | vertical | grid | carousel
  const sectionPadding = storefront.section_padding || "compact";    // compact | comfy | roomy

  // Apply storefront CSS variables at document root so the whole customer surface picks them up.
  useEffect(() => {
    const root = document.documentElement;
    const set = (key, val) => val ? root.style.setProperty(key, val) : root.style.removeProperty(key);
    set("--bb-section-bg", storefront.section_bg);
    set("--bb-card-bg", storefront.card_bg);
    set("--bb-section-title", storefront.section_title_color);
    set("--bb-btn", storefront.button_color);
    set("--bb-radius", storefront.border_radius ? `${storefront.border_radius}px` : null);
  }, [storefront.section_bg, storefront.card_bg, storefront.section_title_color, storefront.button_color, storefront.border_radius]);

  // Sprint 55 — Global Typography: apply body font family + heading vars from
  // ui.card.typography so admins can change fonts platform-wide without redeploy.
  // (Nested under `card` because the backend patch model only whitelists
  // {homepage, card, header, pdp} at the top level.)
  const typo = ui?.card?.typography || {};
  useEffect(() => {
    const root = document.documentElement;
    const set = (key, val) => val ? root.style.setProperty(key, val) : root.style.removeProperty(key);
    set("--bb-font-body", typo.body_font_family || null);
    set("--bb-font-heading", typo.heading_font_family || null);
    set("--bb-font-price", typo.price_font_family || null);
    set("--bb-font-button", typo.button_font_family || null);
    set("--bb-body-size", typo.body_font_size ? `${typo.body_font_size}px` : null);
    set("--bb-body-weight", typo.body_font_weight || null);
    set("--bb-body-tracking", typo.body_letter_spacing != null ? `${typo.body_letter_spacing}px` : null);
    set("--bb-body-leading", typo.body_line_height || null);
    // Apply body font directly for immediate effect across every page.
    if (typo.body_font_family) document.body.style.fontFamily = typo.body_font_family;
    else document.body.style.removeProperty("font-family");
    // Sprint 57 — sync heading font var so .font-display / .font-brand classes pick it up too.
    if (typo.heading_font_family) root.style.setProperty("--bb-font-heading", typo.heading_font_family);
    else root.style.removeProperty("--bb-font-heading");
    if (typo.body_font_family) root.style.setProperty("--bb-font-body", typo.body_font_family);
    else root.style.removeProperty("--bb-font-body");
  }, [typo.body_font_family, typo.heading_font_family, typo.price_font_family, typo.button_font_family,
      typo.body_font_size, typo.body_font_weight, typo.body_letter_spacing, typo.body_line_height]);

  // Store status
  const statusRaw = commerce.store_status || {};
  const mode = statusRaw.mode || "open"; // open | closed | holiday | maintenance
  const acceptOrders = statusRaw.accept_orders !== false; // default true
  const customMessage = statusRaw.custom_message || "";
  const isOpen = mode === "open" && acceptOrders;
  const closedReason = !isOpen
    ? (customMessage || {
        closed: "Store is currently closed. Please check back later.",
        holiday: "We are on a holiday today. Orders will resume tomorrow.",
        maintenance: "Store is under maintenance. We'll be back shortly.",
      }[mode] || (acceptOrders ? "" : "We are not accepting new orders right now."))
    : "";

  // Announcement bar — Marketing.announcement_bar[] (first active row wins).
  const announcement = (marketing.announcement_bar || []).find((a) => a?.is_active && (a.title || a.subtitle)) || null;

  // Delivery zones — commerce.zones[]
  const zones = Array.isArray(commerce.zones) ? commerce.zones.filter((z) => z.is_active !== false) : [];
  const serviceablePincodes = Array.isArray(commerce.serviceable_pincodes)
    ? commerce.serviceable_pincodes.map(String)
    : [];

  const currentZone = useMemo(() => {
    if (!pincode) return null;
    // Zone match: pincodes on the zone OR fallback to any zone containing this pincode in `pincodes[]`.
    const withPin = zones.find((z) => Array.isArray(z.pincodes) && z.pincodes.map(String).includes(String(pincode)));
    if (withPin) return withPin;
    // If no zone but serviceable_pincodes globally lists it, fall back to a synthetic zone from legacy delivery settings.
    if (serviceablePincodes.includes(String(pincode))) {
      return {
        name: "Default zone",
        fee_flat: legacyDelivery.fee_flat ?? 30,
        free_over: legacyDelivery.free_over ?? 500,
        min_order: legacyDelivery.min_order_amount ?? 199,
        eta_minutes: legacyDelivery.prep_minutes ?? 30,
        cod_enabled: true,
        priority: 999,
      };
    }
    return null;
  }, [pincode, zones, serviceablePincodes, legacyDelivery]);

  const isServiceable = useMemo(() => {
    if (!pincode) return true; // don't gate until user has entered one
    if (zones.length === 0 && serviceablePincodes.length === 0) return true; // nothing configured → open service
    return !!currentZone || serviceablePincodes.includes(String(pincode));
  }, [pincode, zones.length, serviceablePincodes, currentZone]);

  const deliveryFee = currentZone?.fee_flat ?? legacyDelivery.fee_flat ?? 30;
  const freeOver = currentZone?.free_over ?? legacyDelivery.free_over ?? 500;
  const minOrder = currentZone?.min_order ?? legacyDelivery.min_order_amount ?? 199;
  const etaMinutes = currentZone?.eta_minutes ?? legacyDelivery.prep_minutes ?? 30;

  const canOrder = isOpen && isServiceable;

  const homepageSections = useMemo(() => {
    // Priority: UI Customization (SUPER_ADMIN) > per-store commerce.homepage > defaults.
    const uiSaved = ui?.homepage?.sections;
    const saved = (Array.isArray(uiSaved) && uiSaved.length > 0) ? uiSaved : commerce.homepage?.sections;
    if (Array.isArray(saved) && saved.length > 0) {
      // Merge saved order/toggle with any newly-added defaults not yet configured.
      const byKey = Object.fromEntries(saved.map((s) => [s.key, s]));
      const merged = saved.filter((s) => s && s.key);
      DEFAULT_HOMEPAGE_SECTIONS.forEach((d) => { if (!byKey[d.key]) merged.push(d); });
      return merged;
    }
    return DEFAULT_HOMEPAGE_SECTIONS;
  }, [commerce.homepage, ui]);

  // Sprint 79 — Global Products-Per-Row for the whole homepage. When set, it
  // applies to every product grid section that hasn't declared its own
  // per_row_mobile / per_row_desktop override. Read from ui.homepage.grid
  // (Website Builder saves it there).
  const homepageGrid = useMemo(() => {
    const g = ui?.homepage?.grid || commerce.homepage?.grid || {};
    return {
      per_row_mobile: Number(g.per_row_mobile) || 0,
      per_row_desktop: Number(g.per_row_desktop) || 0,
    };
  }, [commerce.homepage, ui]);

  // Homepage SEO — read from commerce.homepage.seo (Website Builder saves it here).
  const homepageSeo = useMemo(() => {
    const s = ui?.homepage?.seo || commerce.homepage?.seo || {};
    return {
      title: (s.title || "").toString(),
      description: (s.description || "").toString(),
      keywords: (s.keywords || "").toString(),
    };
  }, [commerce.homepage, ui]);

  const value = useMemo(() => ({
    store, loading, refresh,
    mode, acceptOrders, isOpen, closedReason, customMessage,
    announcement,
    zones, serviceablePincodes, currentZone, isServiceable,
    deliveryFee, freeOver, minOrder, etaMinutes,
    canOrder,
    pincode, setPincode,
    homepageSections,
    homepageSeo,
    homepageGrid,
    commerce, marketing,
    social, storefront, categoryStyle, categoryFont, categoryLayout, sectionPadding,
    ui: ui || {}, // UI Customization blob (defaults are backend-provided)
  }), [store, loading, refresh, mode, acceptOrders, isOpen, closedReason, customMessage,
       announcement, zones, serviceablePincodes, currentZone, isServiceable,
       deliveryFee, freeOver, minOrder, etaMinutes, canOrder, pincode, setPincode,
       homepageSections, homepageSeo, homepageGrid, commerce, marketing, social, storefront, categoryStyle, categoryFont, categoryLayout, sectionPadding, ui]);

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export const useStore = () => useContext(StoreCtx) || {
  isOpen: true, canOrder: true, isServiceable: true, homepageSections: DEFAULT_HOMEPAGE_SECTIONS,
  homepageSeo: { title: "", description: "", keywords: "" },
  homepageGrid: { per_row_mobile: 0, per_row_desktop: 0 },
  zones: [], serviceablePincodes: [], announcement: null, marketing: {}, commerce: {},
  social: {}, storefront: {}, categoryStyle: "circle", categoryFont: "default", categoryLayout: "auto", sectionPadding: "compact",
  ui: {},
};
